import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { JwtLegFiClaims } from '../../../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../../../services/auth/legfi-jwt.service';
import { GrowlerService } from '../../../../shared/growler/growler.service';
import { PlaidService, PlaidSuccessMetaData } from '../../../../../services/plaid.service';
import { MatLegacySlideToggle as MatSlideToggle, MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import conditionalValidator from '../../../../../directives/validators/conditional.validator';
import { PlaidAccount, PlaidToken } from '../../../../../models/entities/plaid-token';
import { PlaidTokenService } from '../../../../../services/plaid-token.service';
import { PlaidSearchComponent, PlaidSearchDialogInput, PlaidSearchDialogResult } from '../plaid-search/plaid-search.component';
import { ConfirmActionDialogComponent, ConfirmActionDialogInput, ConfirmActionDialogResult } from '../../confirm-action-dialog/confirm-action-dialog.component';
import { ConfirmWithSelectionDialogComponent, ConfirmWithSelectionDialogInput, ConfirmWithSelectionDialogResult } from '../../confirm-with-selection-dialog/confirm-with-selection-dialog.component';
import { MatDialogSizes } from '../../../../../enums/mat-dialog-sizes.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UnifiedBankAccount } from '../../../../../models/entities/unified-bank-account';
import moment from 'moment-timezone';
import { SharedModule } from '../../../../shared/shared.module';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyOptionModule as MatOptionModule } from '@angular/material/legacy-core';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { ErrorClassWhenInvalidDirective } from '../../../../../directives/error-class-when-invalid.directive';
import { ControlMessagesComponent } from '../../../../shared/control-messages/control-messages.component';
import { SuperStarComponent } from '../../super-star/super-star.component';

declare var Plaid: any;

export interface BankAccountPropertiesFormValue
{
    id?: number;
    friendlyName: string;
    useForDeposits: boolean;
    routingNumber: string;
    accountNumber: string;
    connectToTransactionFeed: boolean;
    plaidTokenId: number;
    plaidAccountId: string;
}

/**
 * Manages bank account configuration for deposits and transaction feed connections.
 * Orchestrates complex Plaid integration flows for linking multiple banking credentials.
 *
 * Plaid connection hierarchy: Organization can have multiple PlaidTokens (banking logins),
 * each token can expose multiple PlaidAccounts, but each account maps to one UnifiedBankAccount.
 * Users can either add accounts to existing tokens or create new banking connections.
 */
@UntilDestroy()
@Component({
    selector: 'app-bank-account-properties',
    templateUrl: './bank-account-properties.component.html',
    styleUrls: ['./bank-account-properties.component.scss'],
    standalone: true,
    imports: [
        SharedModule,
        MatIconModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatOptionModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatDatepickerModule,
        MatMomentDateModule,
        ErrorClassWhenInvalidDirective,
        ControlMessagesComponent,
        SuperStarComponent,
    ],
})
export class BankAccountPropertiesComponent implements OnInit
{
    @Input() replacementCandidates: UnifiedBankAccount[] = [];
    @Input() updateAccount: UnifiedBankAccount = null;
    @Input() action: 'add_deposit' | 'remove_deposit' | 'link_pick' | 'link_connect' | null;
    @Input() onlyDepositAccounts: boolean;
    @Input() treasuryRestricted: boolean = false;

    @ViewChild('transactionToggle') transactionToggle: MatSlideToggle;

    form: UntypedFormGroup = null;
    jwt: JwtLegFiClaims;

    allTokens: PlaidToken[] = [];
    tokenOptions: PlaidToken[] = [];
    plaidObject: any = null;

    constructor(
            private _dialog: MatDialog,
            private _builder: UntypedFormBuilder,
            private _growler: GrowlerService,
            private _plaidService: PlaidService,
            private _plaidTokens: PlaidTokenService,
    ) {
    }

    ngOnInit() {
        this.jwt = LegFiJwtService.read();

        this.form = this._builder.group({
            friendlyName: [
                this.updateAccount?.friendlyName || '',
                [Validators.required],
            ],
            accountNumber: [
                '',
                [
                    conditionalValidator(() => {
                        return (this.form.controls['useForDeposits'].value && !this.updateAccount?.depositBankAccount) && !this.updateAccount?.last4;
                    }, Validators.required),
                    Validators.minLength(6),
                ],
            ],
            routingNumber: [
                this.updateAccount?.routingNumber,
                [
                    conditionalValidator(() => {
                        return this.form.controls['useForDeposits'].value && !this.updateAccount?.depositBankAccount;
                    }, Validators.required),
                    Validators.minLength(9),
                    Validators.maxLength(9),
                ],
            ],
            useForDeposits: [
                {
                    value: this.onlyDepositAccounts || !!this.updateAccount?.depositBankAccount,
                    disabled: this.onlyDepositAccounts,
                } || false,
            ],
            replacementDepositAccount: [
                null,
                [
                    conditionalValidator(() => {
                        return !this.form.controls['useForDeposits'].value && this.updateAccount?.depositBankAccount;
                    }, Validators.required),
                ],
            ],
            allianceTransactionImportEnabled: [!!this.updateAccount?.allianceTransactionImportEnabled || false],
            allianceTransactionImportAfter: [
                this.updateAccount?.allianceTransactionImportAfter || moment()
                        .startOf('day'),
            ],
            connectToTransactionFeed: [!!this.updateAccount?.plaidToken || false],
            plaidToken: [
                this.updateAccount?.plaidToken,
                [
                    conditionalValidator(() => {
                        return this.form.controls['connectToTransactionFeed'].value;
                    }, Validators.required),
                ],
            ],
            plaidAccount: [
                this.updateAccount?.plaidAccount,
                [
                    conditionalValidator(() => {
                        return this.form.controls['connectToTransactionFeed'].value;
                    }, Validators.required),
                ],
            ],
            plaidImportAfter: [
                this.updateAccount?.plaidImportAfter || moment(),
                [
                    conditionalValidator(() => {
                        return this.form.controls['connectToTransactionFeed'].value;
                    }, Validators.required),
                ],

            ],
        });

        if (this.updateAccount?.depositBankAccount && !this.jwt.superUser) {
            this.form.get('routingNumber').disable();
        }

        // Stripe Treasury orgs may only rename a non-managed account. Lock every other control so
        // banking details and deposit/transaction linkage can't be changed in a way that would
        // break the Stripe Financial Account.
        if (this.treasuryRestricted) {
            Object.keys(this.form.controls).forEach((controlKey) => {
                if (controlKey !== 'friendlyName') {
                    this.form.get(controlKey).disable();
                }
            });
        }

        if (this.action) {
            switch (this.action) {
                case 'add_deposit':
                    this.form.controls['useForDeposits'].setValue(true);
                    break;
                case 'remove_deposit':
                    this.form.controls['useForDeposits'].setValue(false);
                    break;
                case 'link_pick':
                    this.form.controls['connectToTransactionFeed'].setValue(true);
                    break;
                case 'link_connect':
                    this.form.controls['connectToTransactionFeed'].setValue(true);
                    this.launchPlaidIfNoTokens(true);
                    break;
            }
        }

        this._plaidTokens.$tokens.pipe(untilDestroyed(this)).subscribe({
            next: (tokens: PlaidToken[]) => {
                this.allTokens = tokens;
                if (this.form.get('plaidToken').value) {
                    this.form.get('plaidToken')
                            .setValue(this.allTokens.find(t => t.id === this.form.get('plaidToken').value.id));
                }
            },
        });
    }

    /**
     * Updates existing Plaid token to add new accounts from same banking credentials.
     * Handles Plaid Link in update mode to avoid creating duplicate connections.
     */
    updateToken(token: PlaidToken) {
        const plaidConfig = {
            onSuccess: () => {
                this.plaidObject.destroy();
                this.plaidObject = null;
                // select the first new account on the token
                // we need to refresh this token to know what was added
                this._plaidTokens.getToken(token.id).pipe(untilDestroyed(this))
                        .subscribe({
                            next: (updatedToken: PlaidToken) => {
                                const accounts = updatedToken.plaidAccounts.filter((account: PlaidAccount) => !account.unifiedBankAccount);
                                if (accounts.length === 0) {
                                    this._growler.oops('No new accounts were detected. Please try connecting again.');
                                } else {
                                    this.form.controls['plaidAccount'].setValue(accounts[0]);
                                }
                            },
                        });
            },
            onExit: (error: any) => {
                this.plaidObject.destroy();
                if (error !== null) {
                    // an error occurred in plaid
                    this._growler.oops('An error occurred during the linking process. Please try again.');
                } else {
                    // The user exited plaid
                    this._growler.oops('You must complete the Plaid link process to enable transactions. Please try again.');
                }
            },
            token: token.updateLinkToken,
        };

        this.plaidObject = Plaid.create(plaidConfig);
        this.plaidObject.open();
    }

    launchPlaidIfNoTokens(checked: boolean) {
        if (!checked) {
            return;
        }
        if (this.allTokens.length === 0) {
            // launch straight into plaid if we have no tokens at all
            this.connectToBank();
        }
    }

    /**
     * Initiates institution search flow with smart duplicate detection.
     * Prompts user to either update existing connections or create new ones
     * when attempting to connect to an already-linked institution.
     */
    searchBanks() {
        this._dialog.open(PlaidSearchComponent, {
            panelClass: 'min-30',
            data: <PlaidSearchDialogInput>{
                product: 'transactions',
            },
        }).afterClosed().subscribe({
            next: (result: PlaidSearchDialogResult) => {
                if (!result.success) {
                    return;
                }
                // if this is a new institution, continue to connect
                // if we have this bank already, prompt to confirm they want to continue or
                // if they would rather update the existing token
                const alreadyConnectedToBank = this.allTokens.filter(token => token.plaidInstitutionId === result?.selection.institutionId && !token.plaidError);
                if (!alreadyConnectedToBank.length) {
                    return this.connectToBank(result?.selection.institutionId);
                }
                if (alreadyConnectedToBank.length === 1) {
                    this._dialog.open(ConfirmActionDialogComponent, {
                        width: MatDialogSizes.MD,
                        data: <ConfirmActionDialogInput>{
                            title: 'Confirm Bank Selection',
                            content: 'PayHOA already has a connection to ' + result?.selection.name + '. Do you want to add an additional account to your existing connection, or connect a new set of online banking credentials?',
                            confirmButton: 'Add Account to Existing Connection',
                            secondaryButton: 'Connect a New Account',
                        },
                    }).afterClosed().subscribe({
                        next: (confirmResult: ConfirmActionDialogResult) => {
                            if (confirmResult?.success) {
                                if (confirmResult?.secondary) {
                                    return this.connectToBank(result?.selection.institutionId);
                                } else {
                                    return this.updateToken(alreadyConnectedToBank[0]);
                                }
                            }
                        },
                    });
                } else {
                    this._dialog.open(ConfirmWithSelectionDialogComponent, {
                        data: <ConfirmWithSelectionDialogInput>{
                            title: 'Confirm Bank Selection',
                            content: 'PayHOA already has multiple connections to ' + result?.selection.name + '. Do you want to add an additional account to an existing connection, or connect a new online banking login?.',
                            btnContent: 'Add Account to Selected Existing Connection',
                            secondaryBtnContent: 'Connect a New Account',
                            selectionLabel: 'Select Connection to Update',
                            selectionRequired: true,
                            selectionRequiredSecondary: false,
                            selections: alreadyConnectedToBank,
                        },
                    }).afterClosed().subscribe({
                        next: (confirmResult: ConfirmWithSelectionDialogResult) => {
                            if (confirmResult?.success) {
                                if (confirmResult?.secondaryBtnClicked) {
                                    return this.connectToBank(result?.selection.institutionId);
                                } else {
                                    return this.updateToken(<PlaidToken>confirmResult?.selection);
                                }
                            }
                        },
                    });
                }
            },
        });
    }

    connectToBank(institutionId: string = null) {
        this._plaidService.getLinkToken().pipe(untilDestroyed(this)).subscribe({
            next: (token) => {
                const plaidConfig = {
                    onSuccess: (publicToken: any, metadata: PlaidSuccessMetaData) => {
                        this.plaidObject.destroy();
                        this._plaidTokens.createToken(publicToken, metadata.institution.institution_id).pipe(untilDestroyed(this)).subscribe({
                            next: (newPlaidToken: PlaidToken) => {
                                // select the first account on the new token
                                this.form.patchValue({
                                    plaidAccount: newPlaidToken.plaidAccounts[0],
                                });
                            },
                        });
                    },
                    onExit: (error: any) => {
                        // if we don't have any other tokens to pick from, toggle transactions back off
                        if (this.tokenOptions.length === 0) {
                            this.transactionToggle.toggle();
                        }
                        this.plaidObject.destroy();
                        if (error !== null) {
                            // an error occurred in plaid
                            this._growler.oops('An error occurred during the linking process. Please try again.');
                        } else {
                            // The user exited plaid
                            this._growler.oops('You must complete the Plaid link process to enable transactions. Please try again.');
                        }
                    },
                    token: token,
                };

                this.plaidObject = Plaid.create(plaidConfig);
                this.plaidObject.open(institutionId);
            },
            error: () => {
                // error fetching link token
                this._growler.oops('An error occurred initializing your link session. Please try again.');
            },
        });
    }

    isValid(growl: boolean = true) {
        for (const controlKey in this.form.controls) {
            if (!this.form.controls.hasOwnProperty(controlKey)) {
                continue;
            }
            const control = this.form.controls[controlKey];
            control.markAsTouched();
            control.updateValueAndValidity();
        }

        if (!this.form.valid) {
            if (growl) {
                this._growler.error('Error', 'Please enter all required fields');
            }
            return false;
        }
        return true;
    }

    /**
     * Transforms form data for API submission with sensitive field handling.
     * Omits unchanged routing/account numbers for security and only includes
     * modified deposit settings to prevent accidental state changes.
     */
    getFormValue(): BankAccountPropertiesFormValue {
        const request = this.form.getRawValue();

        // Treasury-restricted accounts may only change the display name — never banking details,
        // deposit linkage, or transaction-feed connections that could break the Financial Account.
        if (this.treasuryRestricted) {
            return {friendlyName: request.friendlyName} as BankAccountPropertiesFormValue;
        }

        // don't include the deposit flag if set and not changed
        if (this.updateAccount?.depositBankAccount) {
            if (request.useForDeposits) {
                delete request.useForDeposits;
            }
        }

        // omit the routing and account numbers if editing and unchanged
        if (this.updateAccount) {
            // if the routing number didn't change, remove it from the request
            if (request.routingNumber.trim() === this.updateAccount.routingNumber) {
                delete request.routingNumber;
            }

            // if the account number is empty, it didn't change, remove it
            if (request.accountNumber.trim() === '') {
                delete request.accountNumber;
            }
        }

        // don't include transaction related fields if not changed
        if (request.connectToTransactionFeed) {
            const tokenForSelectedAccount: PlaidToken = this.allTokens.filter(token => {
                return token.plaidAccounts.map(account => account.accountId).includes(request.plaidAccount.accountId);
            })[0];
            request.plaidTokenId = tokenForSelectedAccount.id;
            request.plaidAccountId = request.plaidAccount.accountId;
        }
        delete request.plaidAccount;

        if (request.hasOwnProperty('allianceTransactionImportAfter')) {
            request.allianceTransactionImportAfter = request.allianceTransactionImportAfter.format('YYYY-MM-DD');
        }
        if (request.hasOwnProperty('plaidImportAfter')) {
            request.plaidImportAfter = request.plaidImportAfter.format('YYYY-MM-DD');
        }

        return request;
    }

    updateIfNew() {
        if (this.form.get('plaidAccount').value === 'new-account') {
            this.form.get('plaidAccount').setValue(null);
            this.updateToken(this.form.get('plaidToken').value);
        }
    }

    searchIfNew() {
        if (this.form.get('plaidToken').value === 'new-login') {
            this.form.get('plaidToken').setValue(null);
            this.searchBanks();
        }
    }

    compareTokens(o1: PlaidToken, o2: PlaidToken) {
        return o1 && o2 && o1.id === o2.id;
    }

    compareAccounts(o1: PlaidAccount, o2: PlaidAccount) {
        return o1 && o2 && o1.accountId === o2.accountId;
    }

    trackByReplacementId(_: number, a: UnifiedBankAccount): number {
        return a.id;
    }

    trackByTokenId(_: number, t: PlaidToken): number {
        return t.id;
    }

    trackByPlaidAccountId(_: number, a: PlaidAccount): string {
        return a.accountId;
    }
}
